/**
 * 为了兼容server和client端，通过判断来分辨执行不同逻辑，避免报错
 * IMP: 理论上server和client逻辑应该隔离，当前因为colorFetchV2这种方法存在server和client调用，只能先兼容。
 * FIXME: 后续colorFetchV2做好拆分，就能拆开隔离了
 * IMP: 多域名不支持测试环境和预发环境，在用户个人中心切国家即可实现不跳域名达到此功能
 */
import { isNextServer } from '@/intl-jdi-common-ssr/utils/uaParser';
import cookieUtil from '@/intl-jdi-common-ssr/utils/cookieUtils';
import {
  DefaultHost,
  oldDEDomainSuffix,
  DefaultBetaHost,
  DefaultProtocol,
  SiteCode,
  siteCodeMap,
  countryCodeMap,
  colorApiMap,
  colorApiBetaMap,
  multiDomainMap,
  multiDomainBetaMap,
  multiDomainBeta2Map,
  NLSiteCountries,
  FRSiteCountries,
  DESiteCountries,
  UKSiteCountries,
  multiDomainCountryEnum,
  defaultJDICountry,
  imageApiMap,
  TestImageApi,
  defaultLangMap,
} from './const';
import { getGoogleSiteVerificationCode } from './googleVerification';

export const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';
export const isBeta = process.env.NEXT_PUBLIC_APP_ENV === 'beta';
export const isTest = process.env.NEXT_PUBLIC_APP_ENV === 'test';
export const isDev = process.env.NEXT_PUBLIC_APP_ENV === 'development';

const isBeta2FromHost = (host: string) => {
  return host.includes('beta2');
};

// 从 Headers 获取国家代码
function getCountryCodeFromHeaders(headers: Headers | null): string {
  if (!headers) return '';
  return (
    headers.get('user-client-country') || // Akamai
    headers.get('CF-IPCountry') || // Cloudflare
    headers.get('CloudFront-Viewer-Country') || // AWS CloudFront
    ''
  );
}

export const setTargetDomain = ({
  countryCode,
  pathname,
  langCode,
  currencyCode,
}: {
  countryCode: string;
  pathname?: string;
  langCode?: string;
  currencyCode?: string;
}) => {
  const orgCountryCode = cookieUtil.getCookie('_country');
  const curDomain = getCurrentDomain();
  const targetDomain = getMultiTargetDomain(countryCode);

  const urlObject = new URL(window.location.href);
  const targetDomainObject = new URL(targetDomain);
  urlObject.host = targetDomainObject.host;
  urlObject.protocol = targetDomainObject.protocol;
  urlObject.port = targetDomainObject.port;

  urlObject.pathname = pathname || '/';
  urlObject.search = '';
  urlObject.searchParams.set('noRedirect', '1');
  urlObject.searchParams.set('country', countryCode);
  urlObject.searchParams.set('fromCountry', orgCountryCode);
  if (langCode) {
    urlObject.searchParams.set('lang', langCode);
  }

  if (currencyCode) {
    urlObject.searchParams.set('currency', currencyCode);
  }

  return {
    isSameDomain: curDomain.domain === targetDomain,
    urlObject,
  };
};

function getJDICountryBySiteCode(siteCode: SiteCode) {
  if (siteCode === siteCodeMap['.nl']) {
    return NLSiteCountries[0];
  } else if (siteCode === siteCodeMap['.fr']) {
    return FRSiteCountries[0];
  } else if (siteCode === siteCodeMap['.de']) {
    return DESiteCountries[0];
  } else {
    return defaultJDICountry;
  }
}

export function getSiteCodeByCountryCode(countryCode: string) {
  if (NLSiteCountries.includes(countryCode)) {
    return siteCodeMap['.nl'];
  }
  if (DESiteCountries.includes(countryCode)) {
    return siteCodeMap['.de'];
  }
  if (FRSiteCountries.includes(countryCode)) {
    return siteCodeMap['.fr'];
  }
  return siteCodeMap['.com'];
}

// 获取多站点要素，国家、站点code、货币
export const getJDISiteKeys = (forFetch?: boolean) => {
  // 测试环境没有多域名 单独处理
  if (isTest || isDev) {
    let _country = cookieUtil.getCookie('_country');
    if (!multiDomainCountryEnum.includes(_country)) {
      _country = defaultJDICountry;
    }

    const _site = getSiteCodeByCountryCode(_country);

    return {
      'JDI-Country': _country,
      'JDI-Currency': cookieUtil.getCookie('_currency'),
      'JDI-Site': _site,
      'JDI-Default-Lang': defaultLangMap[_site],
    };
  }

  // 生产环境和预发环境，有多域名
  let host = DefaultHost;
  let jdiCountry = '';
  let siteCode: SiteCode = 'UK-Site';
  let siteCountries = UKSiteCountries;

  if (isNextServer()) {
    try {
      const { headers } = require('next/headers');
      host = headers().get('host') || DefaultHost;

      jdiCountry = cookieUtil.getCookie('_country');
      if (!forFetch && !jdiCountry) {
        jdiCountry = getCountryCodeFromHeaders(headers());
      }
    } catch (e) {
      console.error('Server-side getJDISiteKeys error:', e);
    }
  } else {
    host = window.location.host;
    jdiCountry = cookieUtil.getCookie('_country');
  }
  if (typeof jdiCountry === 'string' && jdiCountry.toUpperCase() === 'UK') {
    jdiCountry = defaultJDICountry;
  }

  for (const [siteSuffix, site] of Object.entries(siteCodeMap)) {
    if (host.includes(siteSuffix)) {
      siteCode = site;
      siteCountries = countryCodeMap[site];
    }
  }

  // 当前国家不在多站点国家内，通过站点来给默认国家值
  if (!siteCountries.includes(jdiCountry)) {
    jdiCountry = getJDICountryBySiteCode(siteCode);
  }

  return {
    'JDI-Country': jdiCountry,
    'JDI-Currency': cookieUtil.getCookie('_currency'),
    'JDI-Site': siteCode,
    'JDI-Default-Lang': defaultLangMap[siteCode],
  };
};

// 测试和预发环境取环境变量中写死的，生产多域名下需要动态获取
export const getColorApiPath = () => {
  const isBrowser = typeof window !== 'undefined';
  const host = getHost();

  let colorApiPath = colorApiMap['.com'];
  if (isProduction) {
    if (isBrowser) {
      for (const [pathSuffix, apiPath] of Object.entries(colorApiMap)) {
        if (host.includes(pathSuffix)) {
          colorApiPath = apiPath;
          return colorApiPath;
        }
      }
    } else {
      colorApiPath = 'http://color-api.joybuy.local';
    }
  } else if (isBeta) {
    for (const [pathSuffix, apiPath] of Object.entries(colorApiBetaMap)) {
      if (host.includes(pathSuffix)) {
        colorApiPath = apiPath;
        return colorApiPath;
      }
    }
  } else {
    colorApiPath = (
      isBrowser ? process.env.NEXT_PUBLIC_COLOR_URL : process.env.COLOR_URL
    ) as string;
  }

  return colorApiPath;
};

export const getImageApiPath = () => {
  const host = getHost();
  let colorApiPath = imageApiMap['.com'];
  if (isProduction || isBeta) {
    for (const [pathSuffix, apiPath] of Object.entries(imageApiMap)) {
      if (host.includes(pathSuffix)) {
        colorApiPath = apiPath;
        return colorApiPath;
      }
    }
  } else {
    colorApiPath = TestImageApi;
  }
  return colorApiPath;
};

const getHost = () => {
  let host = DefaultHost;
  if (isNextServer()) {
    try {
      const { headers } = require('next/headers');
      host = headers().get('host') || DefaultHost;
    } catch (e) {
      console.error('Server-side getColorApiPath error:', e);
    }
  } else {
    host = window.location.host;
  }
  return host;
};

// 获取当前域名
export const getCurrentDomain = () => {
  if (isNextServer()) {
    try {
      const { headers } = require('next/headers');
      const host = headers().get('host') || DefaultHost;
      // np透传上游header，如果X-Proto是HTTP，则是http，如果是SSL，则是https
      const protocol =
        headers().get('X-Proto')?.toLowerCase() === 'http' ? 'http:' : DefaultProtocol;
      const domain = `${protocol}//${host}`;
      return { domain, host, protocol };
    } catch (e) {
      console.error('Server-side getCurrentDomain error:', e);
      return {
        domain: `${DefaultProtocol}//${DefaultHost}`,
        host: DefaultHost,
        protocol: DefaultProtocol,
      };
    }
  }
  return {
    domain: window.location.origin,
    host: window.location.host,
    protocol: window.location.protocol,
  };
};

export const getMultiTargetDomain = (toCountry?: string) => {
  let targetDomain = multiDomainMap.default;
  if (isNextServer()) {
    try {
      const { headers } = require('next/headers');

      // 根据用户所在国家自动跳转域名：https://joyspace.jd.com/pages/7uCB6xd01ja8JmptLah0
      const clientCountry = toCountry || getCountryCodeFromHeaders(headers()).toUpperCase();
      const host = headers().get('host') || DefaultHost;

      if (clientCountry && multiDomainCountryEnum.includes(clientCountry)) {
        let domainMap = multiDomainMap;
        if (isBeta) {
          domainMap = isBeta2FromHost(host) ? multiDomainBeta2Map : multiDomainBetaMap;
        }
        if (NLSiteCountries.includes(clientCountry)) {
          targetDomain = domainMap.NL;
        } else if (FRSiteCountries.includes(clientCountry)) {
          targetDomain = domainMap.FR;
        } else if (DESiteCountries.includes(clientCountry)) {
          targetDomain = domainMap.DE;
        } else {
          targetDomain = domainMap.default;
        }
      }
    } catch (e) {
      console.error('Server-side getMultiTargetDomain error:', e);
    }
  } else {
    if (toCountry && multiDomainCountryEnum.includes(toCountry)) {
      const host = window.location.host;
      let domainMap = multiDomainMap;
      if (isBeta) {
        domainMap = isBeta2FromHost(host) ? multiDomainBeta2Map : multiDomainBetaMap;
      }
      if (NLSiteCountries.includes(toCountry)) {
        targetDomain = domainMap.NL;
      } else if (FRSiteCountries.includes(toCountry)) {
        targetDomain = domainMap.FR;
      } else if (DESiteCountries.includes(toCountry)) {
        targetDomain = domainMap.DE;
      } else {
        targetDomain = domainMap.default;
      }
    }
  }
  return targetDomain;
};

export const getMultiDomainJumpUrl = (href: string): string | null => {
  if (isNextServer()) {
    try {
      const { headers } = require('next/headers');
      const host = headers().get('host') || '';
      const clientCountry = getCountryCodeFromHeaders(headers()).toUpperCase();

      let newDomain = multiDomainMap.default;

      // 只在首页(/)生效，且跳转后到首页（PM郑倩微确认）
      const urlObject = new URL(href);

      // 针对旧域名 .co.de, 重定向到 .de
      if (host.includes(oldDEDomainSuffix)) {
        let newDEHost = multiDomainMap['DE'];
        const host = headers().get('host') || '';
        if (isBeta) {
          newDEHost = isBeta2FromHost(host) ? multiDomainBeta2Map['DE'] : multiDomainBetaMap['DE'];
        }
        urlObject.host = newDEHost;
        return urlObject.href;
      }

      if (
        (host === DefaultHost || host === DefaultBetaHost) &&
        urlObject.pathname === '/' &&
        urlObject.search.includes('enabledomain=1')
      ) {
        newDomain = getMultiTargetDomain();

        // 避免死循环
        if (newDomain.includes(DefaultHost) || newDomain.includes(DefaultBetaHost)) {
          return null;
        }
        return `${newDomain}/?fromCountry=${clientCountry}`;
        // return `${newDomain}${urlObject.pathname}${urlObject.search}`;
      }
      return null;
    } catch (e) {
      console.error('Server-side multiDomainJump error:', e);
      return null;
    }
  } else {
    return null;
  }
};

export const getCurrentGoogleSiteVerification = () => {
  try {
    const currentDomain = getCurrentDomain();
    const host = currentDomain.host;

    return getGoogleSiteVerificationCode(host);
  } catch (error) {
    console.error('Error getting Google Site Verification:', error);
    return getGoogleSiteVerificationCode(DefaultHost);
  }
};
